import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  getPoliciesByType,
  getPolicyActions,
} from '../../../../api/users-api';
import { useState } from 'react';
import { Policy } from '../../../../../types';
import {
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import GeneralDialog from '../../../../shared/general-dialog';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import { UserFormValues } from '../user-schema';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../../shared/shared-toolbar-latest';
import SharedIconButton from '../../../../shared/buttons/shared-icon-button';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Info from '@mui/icons-material/Info';
import PolicyRounded from '@mui/icons-material/PolicyRounded';

interface StepPoliciesProps {
  setValue: UseFormSetValue<UserFormValues>;
  watch: UseFormWatch<UserFormValues>;
}

function CustomToolbar() {
  const queryClient = useQueryClient();
  return (
    <SharedToolbar
      noColumn
      secondaryButtons={
        <SharedIconButton
          variant="refresh"
          onClick={() => {
            queryClient.invalidateQueries({
              queryKey: [UserKeys.Policies, 'newUser'],
            });
          }}
          tooltipLabel="Refresh"
        />
      }
    />
  );
}

export default function StepPolicies({ setValue, watch }: StepPoliciesProps) {
  const [open, setOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);
  const selectedPolicyIds = (watch('policies') || []).map((p) => p.id);
  const [selectedRows, setSelectRows] = useState<number[]>(selectedPolicyIds);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [UserKeys.Policies, 'newUser'],
    queryFn: () => getPoliciesByType('newUser'),
    meta: {
      errorMessage:
        'There was an issue loading policy data. Please refresh and try again.',
    },
  });

  const { data: actionData, isLoading: isActionLoading } = useQuery({
    queryKey: [UserKeys.PolicyActions, selectedPolicy],
    queryFn: () => getPolicyActions(selectedPolicy!),
    enabled: selectedPolicy !== null,
    meta: {
      errorMessage:
        'There was an issue loading policy action data. Please refresh and try again.',
    },
  });

  const columns: GridColDef<Policy>[] = [
    {
      field: 'policyName',
      headerName: 'Name',
      editable: false,
      flex: 1,
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      editable: false,
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'createdDate',
      headerName: 'Created Date',
      editable: false,
      flex: 0.5,
      minWidth: 160,
      type: 'dateTime',
      valueGetter: (_value, row) => {
        if (row.createdDate === 0) return '';
        return new Date(row.createdDate);
      },
      valueFormatter: (value: Date | string) => {
        if (value === '') return '';
        return (value as Date).toLocaleDateString();
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      editable: false,
      width: 200,
      display: 'flex',
      renderCell: (params) => {
        if (!params.row.actions) return;

        return (
          <Stack flexDirection="row" gap={1} sx={{ overflowX: 'auto' }}>
            {params.row.actions.map((action) => (
              <Chip label={action} size="small" color="primary" key={action} />
            ))}
          </Stack>
        );
      },
    },
    {
      field: 'description',
      headerName: 'Description',
      editable: false,
      flex: 1.5,
    },
    {
      type: 'actions',
      field: 'buttonActions',
      width: 50,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={<Info />}
          key={params.row.id}
          label="Info"
          onClick={() => {
            const rowPolicy = params.row as Policy;
            const currentPolicy = data?.find(
              (policy) => policy.id === rowPolicy.id
            );

            if (currentPolicy) {
              setSelectedPolicy(currentPolicy);
              setOpen(true);
            }
          }}
        />,
      ],
    },
  ];

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant="body2">
        Select any policies you'd like to apply to this user.
      </Typography>
      <SharedDataGridClient
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays('Policies', <PolicyRounded />),
        }}
        rows={data || []}
        columns={columns}
        disableColumnMenu
        checkboxSelection
        selectedRows={selectedRows}
        onRowSelectionModelChange={(newModel) => {
          const idArray = Array.from(newModel.ids).map(Number);
          setSelectRows(idArray);
          setValue(
            'policies',
            idArray
              .map((id) => data?.find((policy) => policy.id === id))
              .filter((policy): policy is Policy => policy !== undefined)
          );
        }}
        loading={isLoading || isFetching}
        sx={{
          mt: 2,
          flexGrow: 1,
          minHeight: 0,
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
      />
      <GeneralDialog
        open={open}
        handleClose={() => setOpen(false)}
        title="Action Info"
        dialogContent={
          <Box>
            <Stack gap={1}>
              <Stack>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {selectedPolicy?.policyName}
                </Typography>

                <Typography variant="caption">
                  {selectedPolicy?.description}
                </Typography>
              </Stack>

              <Stack
                gap={2}
                sx={{
                  p: 1,
                  maxHeight: 200,
                  maxWidth: 500,
                  mx: 'auto',
                }}
              >
                {isActionLoading ? (
                  <>
                    <Skeleton variant="rounded" height={100} width={350} />
                    <Skeleton variant="rounded" height={100} width={350} />
                  </>
                ) : (
                  actionData?.actions.map((action) => {
                    const object =
                      actionData?.actionHash[action.replace(/ /g, '_')]
                        .actionData;
                    const keys = Object.keys(object);

                    return (
                      <Card
                        key={action}
                        sx={{ minHeight: 100, minWidth: 350 }}
                        variant="outlined"
                      >
                        <CardContent>
                          <Typography
                            sx={{
                              fontWeight: 'bold',
                            }}
                            variant="body2"
                          >
                            {action}
                          </Typography>
                          {keys.map((key) => {
                            return (
                              <Stack key={key} sx={{ ml: 0.4, p: 0 }}>
                                <Typography variant="caption">
                                  <Typography
                                    variant="caption"
                                    sx={{ textTransform: 'capitalize' }}
                                  >
                                    - {key.replace(/([A-Z])/g, ' $1').trim()}:{' '}
                                  </Typography>
                                  {Array.isArray(object[key])
                                    ? object[key].join(', ')
                                    : object[key]}
                                </Typography>
                              </Stack>
                            );
                          })}
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </Stack>
            </Stack>
          </Box>
        }
        primaryButton={{
          title: 'Close',
          loading: false,
          disabled: false,
          action: () => {
            setOpen(false);
            setSelectedPolicy(null);
          },
        }}
      />
    </Box>
  );
}
